import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Phone, Clock, ArrowRight, Loader2 } from "lucide-react";
import { Reveal, SectionTag, KineticLine } from "@/components/Reveal";
import { products, CONTACT } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", email: "", company: "", product: "", message: "" };

const inputCls =
  "w-full bg-white border border-gray-300 px-4 py-3.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-navy focus:border-navy transition-shadow duration-200";

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry sent. Our trading desk will respond within one business day.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Could not send your enquiry. Please try WhatsApp instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* PAGE HERO */}
      <section className="relative pt-40 pb-24 bg-navy noise overflow-hidden" data-testid="contact-hero">
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="font-tech text-xs uppercase tracking-[0.3em] text-copper mb-6"
          >
            Contact
          </motion.p>
          <h1 className="font-display font-black uppercase text-white tracking-tighter leading-[0.95] text-5xl sm:text-6xl lg:text-7xl" data-testid="contact-headline">
            <KineticLine text="Let's talk" delay={0.15} />
            <KineticLine text="tonnage." delay={0.3} className="text-copper" />
          </h1>
        </div>
      </section>

      <section className="py-24 md:py-32" data-testid="contact-main-section">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-5 gap-16">
          {/* INFO */}
          <div className="lg:col-span-2">
            <SectionTag no="01" label="Reach the Desk" />
            <Reveal>
              <h2 className="font-display font-black text-navy tracking-tighter text-4xl leading-[1.02]">
                One message away from your next shipment.
              </h2>
            </Reveal>
            <div className="mt-12 space-y-8">
              <Reveal delay={0.1}>
                <div className="flex gap-4" data-testid="contact-address-block">
                  <MapPin size={20} className="text-copper shrink-0 mt-1" />
                  <div>
                    <p className="font-tech text-[11px] uppercase tracking-[0.25em] text-gray-500 mb-2">Registered Office</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{CONTACT.address}</p>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="flex gap-4" data-testid="contact-phone-block">
                  <Phone size={20} className="text-copper shrink-0 mt-1" />
                  <div>
                    <p className="font-tech text-[11px] uppercase tracking-[0.25em] text-gray-500 mb-2">Phone & WhatsApp</p>
                    <a href={CONTACT.phoneHref} data-testid="contact-phone-link" className="text-sm text-gray-700 hover:text-copper transition-colors duration-200">
                      {CONTACT.phone}
                    </a>
                  </div>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="flex gap-4" data-testid="contact-hours-block">
                  <Clock size={20} className="text-copper shrink-0 mt-1" />
                  <div>
                    <p className="font-tech text-[11px] uppercase tracking-[0.25em] text-gray-500 mb-2">Trading Hours</p>
                    <p className="text-sm text-gray-700">Monday – Friday · 9:00 – 18:00 GST</p>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          {/* FORM */}
          <Reveal delay={0.1} className="lg:col-span-3">
            <form
              onSubmit={submit}
              className="border border-gray-200 bg-white p-8 md:p-12"
              data-testid="contact-form"
            >
              <p className="font-tech text-[11px] uppercase tracking-[0.25em] text-gray-500 mb-8">
                Enquiry Form — response within one business day
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block font-tech text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Full Name *</label>
                  <input id="name" required minLength={2} value={form.name} onChange={set("name")} className={inputCls} placeholder="Your name" data-testid="contact-input-name" />
                </div>
                <div>
                  <label htmlFor="email" className="block font-tech text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Business Email *</label>
                  <input id="email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="you@company.com" data-testid="contact-input-email" />
                </div>
                <div>
                  <label htmlFor="company" className="block font-tech text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Company</label>
                  <input id="company" value={form.company} onChange={set("company")} className={inputCls} placeholder="Company name" data-testid="contact-input-company" />
                </div>
                <div>
                  <label htmlFor="product" className="block font-tech text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Product Interest</label>
                  <select id="product" value={form.product} onChange={set("product")} className={inputCls} data-testid="contact-select-product">
                    <option value="">Select a product…</option>
                    {products.map((p) => (
                      <option key={p.name} value={p.name}>{p.name}</option>
                    ))}
                    <option value="Other / Custom Grade">Other / Custom Grade</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label htmlFor="message" className="block font-tech text-[11px] uppercase tracking-[0.2em] text-gray-500 mb-2">Message *</label>
                  <textarea id="message" required minLength={5} rows={5} value={form.message} onChange={set("message")} className={inputCls} placeholder="Grade, volume, destination port, target price…" data-testid="contact-input-message" />
                </div>
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="contact-submit-button"
                className="group mt-8 inline-flex items-center gap-3 bg-navy text-white font-tech text-xs uppercase tracking-[0.2em] px-8 py-4 hover:bg-copper disabled:opacity-60 transition-colors duration-300"
              >
                {sending ? (
                  <>Sending <Loader2 size={15} className="animate-spin" /></>
                ) : (
                  <>Send Enquiry <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" /></>
                )}
              </button>
            </form>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
